/**
 * Centralized Template ID Constants
 *
 * All esignatures.io template IDs are managed here to ensure consistency
 * across the application. Update this file when adding, removing, or
 * modifying template references.
 */

export const TEMPLATE_IDS = {
  // Cost Analysis & Optimization (Savings / Schooley Mitchell funnel)
  COST_ANALYSIS_OPTIMIZATION: "965d238e-4b59-44c6-91d2-5d3990d3abdd",

  // Grants funnel — DO NOT mix with COST_ANALYSIS_OPTIMIZATION
  GRANTS_ANALYSIS_SERVICE_AGREEMENT: "4caaca70-666f-46a3-8681-4d55278bf36a",

  // Legal & Compliance
  NDA: "95672725-1749-40f8-8d86-af3ac59d79e3",
  NDA_NON_COMPETE_CONTRACTORS: "2d3e6fc7-cf42-40b3-86a7-157eb75df67e",

  // Service Agreements
  // DEPRECATED: 404s in eSignatures (template deleted) — do not offer. See issue #2077.
  SERVICE_AGREEMENT: "0990d50c-28de-4981-8c80-ff33d5fdf70b",
  SAMPLE_SERVICE_AGREEMENT: "5df0e59f-d4d9-4f2c-8ea1-4d79d7cf8d01",
  MASTER_SERVICE_AGREEMENT: "d71a25c5-0eb7-4a85-acbc-0c0a7fd51e22",
  // "WTC Project Statement of Work — Combined MSA + Project" — the MSA clients
  // actually sign (9 real sends). See issue #2077.
  COMBINED_MSA_PROJECT: "f998904a-2d89-4d1b-ad62-403737c7da24",
  SCHOOLEY_MITCHELL_SERVICE_AGREEMENT: "4fb5cf69-8e69-44a8-8ed8-2aa38b1352d3",

  // Consulting Agreements
  EMPLOYMENT_CONTRACT: "478566cb-0459-49af-9616-b8681584baae",
  // DEPRECATED: both 404 in eSignatures (templates deleted / out of date) — do
  // not offer. TECHNICAL_SERVICES_… is superseded and referenced by title only
  // in the device-procurement templates. See issue #2077.
  SAMPLE_CONSULTING_AGREEMENT: "9d065188-5968-4e9a-b712-3af0af4e89b0",
  TECHNICAL_SERVICES_CONSULTING_AGREEMENT_2025:
    "dd8be68f-dce3-478c-89ff-1bd20e074308",

  // Employment
  EMPLOYEE_HANDBOOK: "b06f9fc5-eae0-4328-a311-197732a788c1",
  AT_WILL_EMPLOYMENT_AGREEMENT_PART_TIME: "3b94242b-a1a5-435d-90b6-f892f4f241af",

  // Device Procurement
  DEVICE_PROCUREMENT_DEALS_TEMPLATE: "6fdb6f78-2d30-4d7e-8da3-8ca4c48124b3",
  DEVICE_PROCUREMENT_NEW_ONE_TIME_PAYMENT: "e77f94c6-597b-41a3-be07-d4b1345aff1a",
  DEVICE_PROCUREMENT_NEW_FINANCED_36_MONTHS: "b03921f3-8495-40e4-ae6c-099b0a6c8166",
  DEVICE_PROCUREMENT_USED_ONE_TIME_PAYMENT: "40121145-0674-4287-8e41-1562b978b439",
  DEVICE_PROCUREMENT_USED_FINANCED_36_MONTHS: "04046dd4-8445-42d1-b99c-01ad7771b365",

  // Website Services
  WEBSITE_DESIGN_QUOTE: "93ce0d6f-f284-4d42-acc6-bc404d4a42cd",
  WEBSITE_IMPLEMENTATION_SIMPLIFIED: "e0735f33-dbfa-4368-a4d5-ef77d75227d9",

  // Production Agreements
  CLIENT_FACTS_PRODUCTION_AGREEMENT: "c2a389ba-53cd-4bfd-b17d-d0930dba1fb8",
  HEAD_OFFICE_PRODUCTION_AGREEMENT: "42595d35-06f4-4c6d-8764-9570f73e272d",

  // Statements of Work
  STATEMENT_OF_WORK_AGREEMENT: "56907f90-3599-433b-bab7-55661b5bdea4",

  // Grants Addenda — amend GRANTS_ANALYSIS_SERVICE_AGREEMENT (the Grant Writing
  // Services Agreement). They are sent IN ADDITION TO that agreement, never
  // instead of it, and neither takes a grant/category selection.
  // Addendum B is OPTIONAL and separately priced ($75/yr) — do not bundle it by
  // default; see MERIDIAN_CONTRACT_TEMPLATES.grants_addendum_management_renewal.
  // RECURRING (annual $75 grant management + renewal) — see
  // recurring-billing-gap-sweep's RECURRING_TEMPLATE_MATCHERS.
  GRANTS_ADDENDUM_A_CLIENT_TERMS: "3aad8dd8-d3db-42b4-8082-58dae7889df9",
  GRANTS_ADDENDUM_B_MANAGEMENT_RENEWAL: "ba4de2bf-cc8c-406b-83c0-4eb48b9acd04",

  // Hosting — RECURRING (monthly). Not yet registered in MERIDIAN_CONTRACT_TEMPLATES
  // or ENABLED_TEMPLATE_IDS (no `send_contract` / wizard path sends it today), but
  // real signed copies exist in `contracts` (matched there by template_name text —
  // that table has no template_id column). See
  // recurring-billing-gap-sweep's RECURRING_TEMPLATE_MATCHERS, and the
  // `contract-template-selection` rule (wtc-sm-work repo) which names this template.
  MANAGED_WORDPRESS_HOSTING: "95fecdf9-c8e2-48b0-8736-3a36b2dabf78",
} as const;

/**
 * All enabled template IDs in order
 * Used by TemplateManager to initialize the enabled templates list
 */
export const ENABLED_TEMPLATE_IDS = [
  TEMPLATE_IDS.COST_ANALYSIS_OPTIMIZATION,
  TEMPLATE_IDS.GRANTS_ANALYSIS_SERVICE_AGREEMENT,
  TEMPLATE_IDS.NDA,
  TEMPLATE_IDS.EMPLOYMENT_CONTRACT,
  TEMPLATE_IDS.EMPLOYEE_HANDBOOK,
  TEMPLATE_IDS.AT_WILL_EMPLOYMENT_AGREEMENT_PART_TIME,
  TEMPLATE_IDS.NDA_NON_COMPETE_CONTRACTORS,
  TEMPLATE_IDS.DEVICE_PROCUREMENT_DEALS_TEMPLATE,
  TEMPLATE_IDS.WEBSITE_DESIGN_QUOTE,
  // Grants addenda (Addendum B is optional/priced — see meridian-templates.ts).
  TEMPLATE_IDS.GRANTS_ADDENDUM_A_CLIENT_TERMS,
  TEMPLATE_IDS.GRANTS_ADDENDUM_B_MANAGEMENT_RENEWAL,
  TEMPLATE_IDS.WEBSITE_IMPLEMENTATION_SIMPLIFIED,
  TEMPLATE_IDS.CLIENT_FACTS_PRODUCTION_AGREEMENT,
  TEMPLATE_IDS.HEAD_OFFICE_PRODUCTION_AGREEMENT,
  TEMPLATE_IDS.DEVICE_PROCUREMENT_NEW_ONE_TIME_PAYMENT,
  TEMPLATE_IDS.SAMPLE_SERVICE_AGREEMENT,
  TEMPLATE_IDS.COMBINED_MSA_PROJECT,
  TEMPLATE_IDS.STATEMENT_OF_WORK_AGREEMENT,
  TEMPLATE_IDS.MASTER_SERVICE_AGREEMENT,
  TEMPLATE_IDS.DEVICE_PROCUREMENT_NEW_FINANCED_36_MONTHS,
  TEMPLATE_IDS.DEVICE_PROCUREMENT_USED_ONE_TIME_PAYMENT,
  TEMPLATE_IDS.DEVICE_PROCUREMENT_USED_FINANCED_36_MONTHS,
  TEMPLATE_IDS.SCHOOLEY_MITCHELL_SERVICE_AGREEMENT,
  // Removed (404 in eSignatures, see #2077): SERVICE_AGREEMENT,
  // SAMPLE_CONSULTING_AGREEMENT, TECHNICAL_SERVICES_CONSULTING_AGREEMENT_2025.
] as const;
